import React, { useCallback, useContext, useRef } from 'react'
import cn from 'classnames'

import { TypeItemFeatureHighlight } from '../../lib/types'
import { Background } from '../../lib/types/background'
import { FeatureHighlight } from '../item/feature-highlight'
import { FeatureHighlightProvider } from '../../contexts/feature-highlight-context'
import { SingleColumnLayout } from '../layout/single-column'
import { ContentfulEntrySys } from '../renderer'
import { containedImageStyle } from '../ui-lib/image'
import { FixedSpacer } from '../ui-lib/spacer'
import { PageContext } from '../../contexts/page-context'
import { CompactSectionsContext } from '../../contexts/compact-sections-context'
import { SectionThemeContext } from '../../contexts/section-theme-context'
import { useBackgroundStyle } from './theme'
import { useBrowserService } from '../../lib/hooks'
import { useToolLayout } from '../../contexts/tool-layout-context'
import { TypeAlignment } from '../../lib/types/alignment'
import { SectionContext } from '../../contexts/section-context'
import { AlignmentContext, DefaultAlignment } from '../../contexts/alignment-context'
import { usePageTheme } from '../../contexts/page-theme-context'
import { useFirstEnterViewport } from '../ui-lib/hooks/viewport-tracker'
import { gnarSpec } from '../../lib/tracking/gnar'

import styles from './page-section.module.scss'
import pageSectionStyles from '../ui-lib/page-section/page-section.module.scss'
import { getBackgroundAsset } from '@components/background-asset'

export interface PageSectionProps {
  internalName: string
  sysStructure: ContentfulEntrySys
  className?: string
  children?: React.ReactNode
  background?: Background
  heading?: TypeItemFeatureHighlight
  alignment?: TypeAlignment
  isRemovedVerticalPadding?: boolean
  isToolsRedesign2_XEnabled?: boolean
  overrideOnFirstEnterViewport?: () => void
  overrideFirstEnterViewportThreshold?: number
  backgroundAssetType?: string
}

export const getSectionAnchorId = (sysStructure?: ContentfulEntrySys): string | undefined => {
  const id = sysStructure?.id
  const contentTypeId = sysStructure?.contentType?.sys?.id

  return id && contentTypeId ? `${contentTypeId}_${id}` : undefined
}

const DEFAULT_FIRST_ENTER_VIEWPORT_THRESHOLD = 0.5

export const PageSection: React.FC<PageSectionProps> = ({
  internalName,
  sysStructure,
  className,
  children,
  background,
  heading,
  alignment,
  isRemovedVerticalPadding,
  isToolsRedesign2_XEnabled = false,
  overrideOnFirstEnterViewport,
  overrideFirstEnterViewportThreshold,
  backgroundAssetType,
}) => {
  const sectionContentContainerRef = useRef<HTMLDivElement>(null)
  const anchorId = getSectionAnchorId(sysStructure)
  const { pageId, slug, isSectionScrollTrackingEnabled } = useContext(PageContext)
  const isCompact = useContext(CompactSectionsContext)
  const { isHero } = useContext(SectionContext)
  const { pageWidth } = usePageTheme()
  const theme = useContext(SectionThemeContext)
  const backgroundStyle = useBackgroundStyle(theme, background?.color)
  const { isMobile } = useBrowserService()
  const isToolLayout = useToolLayout()

  const featureHighlightHeading: TypeItemFeatureHighlight | undefined = heading
    ? {
        ...heading,
        fields: {
          ...heading.fields,
          description: isToolLayout && isMobile ? undefined : heading.fields.description,
        },
      }
    : undefined

  const toolFeatureHighlightHeading: TypeItemFeatureHighlight | undefined = heading
    ? {
        ...heading,
        fields: {
          internalName: heading.fields.internalName,
          description: heading.fields.description, // Keep only description for bottom section
        },
      }
    : undefined

  const onFirstEnter = useCallback(() => {
    if (overrideOnFirstEnterViewport) {
      overrideOnFirstEnterViewport()
    } else {
      if (isSectionScrollTrackingEnabled) {
        gnarSpec.sectionScroll(internalName, pageId, slug)
      }
    }
  }, [internalName, isSectionScrollTrackingEnabled, overrideOnFirstEnterViewport, pageId, slug])

  useFirstEnterViewport(
    sectionContentContainerRef,
    onFirstEnter,
    overrideFirstEnterViewportThreshold ?? DEFAULT_FIRST_ENTER_VIEWPORT_THRESHOLD
  )

  const hasBackgroundAssetType = Boolean(backgroundAssetType)
  const { top: TopBar, background: Background, bottom: BottomBar } = getBackgroundAsset(backgroundAssetType)

  return (
    <div
      className={cn(
        pageWidth === '1280' ? pageSectionStyles.pagePaddingContainer1280 : pageSectionStyles.pagePaddingContainer1080,
        ...backgroundStyle,
        {
          [pageSectionStyles.pageWithBackgroundAsset]: hasBackgroundAssetType,
        }
      )}
      data-component-id={internalName}
    >
      <Background />
      <div
        ref={sectionContentContainerRef}
        id={anchorId}
        className={cn(
          pageSectionStyles.pageContentContainer,
          containedImageStyle,
          {
            [pageSectionStyles.pageContentWidth1080Container]: pageWidth !== '1280',
            [pageSectionStyles.pageContentWidth1280Container]: pageWidth === '1280',
            [pageSectionStyles.pageWithBackgroundAsset]: hasBackgroundAssetType,
            [styles.pageSectionClearPadding]: isRemovedVerticalPadding,
            [styles.pageSectionCompact]: isCompact && !isRemovedVerticalPadding,
            [styles.pageSection]: !isCompact && !isRemovedVerticalPadding,
          },
          className
        )}
      >
        <TopBar />

        {featureHighlightHeading && (
          <FeatureHighlightProvider value={{ titleStyle: 'T1' }}>
            <AlignmentContext.Provider value={alignment?.fields ?? DefaultAlignment}>
              <SingleColumnLayout>
                <FeatureHighlight
                  structure={featureHighlightHeading}
                  titleTag={isHero ? 'h1' : 'h2'}
                  isToolsRedesign2_XEnabled={isToolsRedesign2_XEnabled}
                />
                <FixedSpacer multiplier={2} />
              </SingleColumnLayout>
            </AlignmentContext.Provider>
          </FeatureHighlightProvider>
        )}

        {children}

        {isToolLayout && isMobile && toolFeatureHighlightHeading?.fields.description && (
          <FeatureHighlightProvider value={{ titleStyle: 'T1' }}>
            <AlignmentContext.Provider value={alignment?.fields ?? DefaultAlignment}>
              <SingleColumnLayout>
                <FixedSpacer multiplier={2} />
                <FeatureHighlight structure={toolFeatureHighlightHeading} />
              </SingleColumnLayout>
            </AlignmentContext.Provider>
          </FeatureHighlightProvider>
        )}

        <BottomBar />
      </div>
    </div>
  )
}
